import { X } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useCreateCustomRiskEditRequest } from "@/api/safety/custom-risk-edit-request";
import { Button } from "@/components/base/buttons/button";
import { Dialog, DialogClose, DialogTitle, ScrollableDialogContent } from "@/components/ui/dialog";
import { error as toastError, success as toastSuccess } from "@/providers/ToastProvider";
import { ModalStepper } from "../RequestCustomRiskModal/ModalStepper";
import { StartFlaggingStep } from "./StartFlaggingStep";
import { StopFlaggingStep } from "./StopFlaggingStep";

export type CustomRiskEditRequestForm = {
  stopFlagging: string;
  startFlagging: string;
};

const INITIAL_FORM: CustomRiskEditRequestForm = {
  stopFlagging: "",
  startFlagging: "",
};

const STEP_LABELS = ["Stop flagging", "Start flagging"] as const;
type StepIndex = 0 | 1;

function buildSteps(currentStep: StepIndex) {
  return STEP_LABELS.map((label, i) => ({
    title: label,
    status: (i < currentStep ? "complete" : i === currentStep ? "current" : "incomplete") as
      | "complete"
      | "current"
      | "incomplete",
  }));
}

function validateStep(step: StepIndex, form: CustomRiskEditRequestForm): Record<string, string> {
  const errors: Record<string, string> = {};
  if (step === 0 && !form.stopFlagging.trim()) {
    errors.stopFlagging = "Please give at least one example";
  }
  if (step === 1 && !form.startFlagging.trim()) {
    errors.startFlagging = "Please tell us what to start flagging";
  }
  return errors;
}

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  risk: { id: string; name: string };
};

export function RequestCustomRiskEditModal({ open, onOpenChange, risk }: Props) {
  const [currentStep, setCurrentStep] = useState<StepIndex>(0);
  const [form, setForm] = useState<CustomRiskEditRequestForm>(INITIAL_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { mutateAsync, isPending } = useCreateCustomRiskEditRequest();

  const steps = useMemo(() => buildSteps(currentStep), [currentStep]);

  const handleChange = useCallback((patch: Partial<CustomRiskEditRequestForm>) => {
    setForm((prev) => ({ ...prev, ...patch }));
    setErrors((prev) => {
      const next = { ...prev };
      for (const key of Object.keys(patch)) delete next[key];
      return next;
    });
  }, []);

  const handleNext = useCallback(() => {
    const stepErrors = validateStep(0, form);
    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      return;
    }
    setErrors({});
    setCurrentStep(1);
  }, [form]);

  const handlePrevious = useCallback(() => {
    setErrors({});
    setCurrentStep(0);
  }, []);

  const resetState = useCallback(() => {
    setForm(INITIAL_FORM);
    setCurrentStep(0);
    setErrors({});
  }, []);

  const handleSubmit = useCallback(async () => {
    const stepErrors = validateStep(1, form);
    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      return;
    }
    try {
      await mutateAsync({
        risk,
        stopFlagging: form.stopFlagging,
        startFlagging: form.startFlagging,
      });
      toastSuccess("Request sent successfully. Please allow up to five (5) working days for this to be built.");
      onOpenChange(false);
      resetState();
    } catch (err) {
      toastError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    }
  }, [form, mutateAsync, onOpenChange, resetState, risk]);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) resetState();
      onOpenChange(next);
    },
    [onOpenChange, resetState],
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {/* [&>button]:hidden suppresses the built-in close button rendered by ScrollableDialogContent */}
      <ScrollableDialogContent className="w-[750px] max-w-[750px] [&>button]:hidden">
        <div className="-mx-6 px-6 border-b border-secondary pb-6 relative flex gap-5">
          <div className="w-[90%]">
            <DialogTitle className="text-base font-semibold text-[#181D27] pr-10">
              Request a change to your custom risk
            </DialogTitle>
            <p className="mt-1 text-sm text-tertiary">
              Not flagging quite the way you'd like?
              <br />
              Tell us what to adjust and our team will fine-tune the model for you.
            </p>
          </div>
          <DialogClose className="flex-1 text-gray-400 hover:opacity-75 transition-opacity focus:outline-none">
            <X className="size-5" />
            <span className="sr-only">Close</span>
          </DialogClose>
        </div>

        <div className="-mx-6 px-6 border-b border-secondary py-5">
          <ModalStepper steps={steps} variant="status" />
        </div>

        <div className="-mx-6 px-6 min-h-[300px] border-b border-secondary py-5">
          {currentStep === 0 && (
            <StopFlaggingStep riskName={risk.name} form={form} onChange={handleChange} errors={errors} />
          )}
          {currentStep === 1 && <StartFlaggingStep form={form} onChange={handleChange} errors={errors} />}
        </div>

        <div className="mt-6 flex items-center justify-between">
          <Button color="secondary" size="md" onClick={handlePrevious} isDisabled={currentStep === 0}>
            Previous
          </Button>

          {currentStep < 1 ? (
            <Button color="primary" size="md" onClick={handleNext}>
              Next
            </Button>
          ) : (
            <Button color="primary" size="md" onClick={handleSubmit} isLoading={isPending}>
              Submit request
            </Button>
          )}
        </div>
      </ScrollableDialogContent>
    </Dialog>
  );
}
